import { useEffect, useState } from 'react';
import { LoaderCircle, SlidersHorizontal, CalendarRange } from 'lucide-react';
import { api, eur, num } from '../../lib/project';

// Annual sales view for the "Quanto vendo" step. Replaces the monthly 12x6
// table: shows units per model per year (1/2/3) for the active scenario plus
// the resulting annual projection (revenue / EBITDA). Editing happens in the
// unified "Pressupostos & cenários" tab (onEdit).
export function AnnualSalesView({ onEdit }) {
  const [scenarios, setScenarios] = useState(null);
  const [active, setActive] = useState('');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get('/acquisition/all');
        if (cancelled) return;
        setScenarios(data.scenarios || {});
        const keys = Object.keys(data.scenarios || {});
        setActive((a) => (a && keys.includes(a) ? a : keys[0] || ''));
        setError('');
      } catch {
        if (!cancelled) setError('Não foi possível carregar as vendas anuais.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (busy) return <p className="pressupostos-loading" data-testid="annual-sales-loading"><LoaderCircle size={16} className="spin" /> A carregar vendas anuais…</p>;
  if (error) return <p role="alert" className="editor-validation-error" data-testid="annual-sales-error">{error}</p>;

  const keys = Object.keys(scenarios || {});
  const sc = scenarios?.[active];
  const years = sc?.years || [];
  const models = years[0]?.per_model || [];

  return (
    <div className="annual-sales" data-testid="annual-sales-view">
      <div className="annual-sales-head">
        <div>
          <span className="section-mini-label"><CalendarRange size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />VENDAS ANUAIS · ANOS 1 · 2 · 3</span>
          <p className="annual-sales-note">A previsão passou a ser <strong>anual</strong>: unidades por modelo em cada ano, por cenário. Edite os valores e os cenários no separador «Pressupostos &amp; cenários».</p>
        </div>
        {onEdit && (
          <button type="button" className="annual-sales-edit-btn" data-testid="annual-sales-edit" onClick={onEdit}>
            <SlidersHorizontal size={14} /> Editar vendas anuais e cenários
          </button>
        )}
      </div>

      <div className="annual-sales-tabs" role="tablist" data-testid="annual-sales-tabs">
        {keys.map((key) => (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={active === key}
            className={`annual-sales-tab ${active === key ? 'selected' : ''}`}
            data-testid={`annual-sales-tab-${key}`}
            onClick={() => setActive(key)}
          >
            <span className="annual-sales-tab-dot" style={{ background: scenarios[key].color || '#334155' }} aria-hidden="true" />
            {scenarios[key].label || key}
          </button>
        ))}
      </div>

      {sc && (
        <div className="annual-sales-scroll">
          <table className="cmp-table annual-sales-table" data-testid="annual-sales-table">
            <thead>
              <tr>
                <th>Modelo</th>
                {years.map((y) => <th key={y.year}>Ano {y.year}</th>)}
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.id} data-testid={`annual-sales-row-${m.id}`}>
                  <th scope="row">{m.name}</th>
                  {years.map((y) => {
                    const pm = (y.per_model || []).find((x) => x.id === m.id);
                    return <td key={y.year}>{num(pm?.units || 0)}</td>;
                  })}
                </tr>
              ))}
              <tr className="annual-sales-total-row">
                <th scope="row">Total unidades</th>
                {years.map((y) => <td key={y.year}><strong>{num(y.units)}</strong></td>)}
              </tr>
              <tr className="annual-sales-proj-row">
                <th scope="row">Receita s/ IVA</th>
                {years.map((y) => <td key={y.year}>{eur(y.revenue_net, 0)}</td>)}
              </tr>
              <tr className="annual-sales-proj-row">
                <th scope="row">EBITDA</th>
                {years.map((y) => <td key={y.year}><strong>{eur(y.ebitda, 0)}</strong></td>)}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
